import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../../../firebase";
import { doc, getDoc } from "firebase/firestore";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";

const MachineDetails = () => {
  const { categoryId, machineId } = useParams();
  const navigate = useNavigate();

  const [machine, setMachine] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMachine();
  }, [categoryId, machineId]);

  const loadMachine = async () => {
    setLoading(true);

    try {
      // Get machine
      const machineRef = doc(
        db,
        "categories",
        categoryId,
        "machines",
        machineId
      );

      const machineSnapshot = await getDoc(machineRef);

      if (machineSnapshot.exists()) {
        setMachine({
          id: machineSnapshot.id,
          ...machineSnapshot.data(),
        });
      }

      // Get category
      const categoryRef = doc(db, "categories", categoryId);
      const categorySnapshot = await getDoc(categoryRef);

      if (categorySnapshot.exists()) {
        setCategory({
          id: categorySnapshot.id,
          ...categorySnapshot.data(),
        });
      }
    } catch (error) {
      console.error("Error loading machine:", error);
    }

    setLoading(false);
  };

  /* =========================
     LOADING
  ========================= */
  if (loading) {
    return (
      <>
        <Header />

        <div className="min-h-screen bg-[#f6f9fc] flex items-center justify-center px-4">
          <div className="relative">
            <div className="absolute -inset-4 rounded-full bg-blue-400/10 blur-xl" />

            <div className="relative w-16 h-16 rounded-full bg-white border border-slate-200 shadow-lg flex items-center justify-center">
              <div className="w-8 h-8 rounded-full border-[3px] border-blue-100 border-t-blue-600 animate-spin" />
            </div>
          </div>

          <p className="absolute mt-28 text-sm font-medium text-slate-500">
            Loading machine details...
          </p>
        </div>

        <Footer />
      </>
    );
  }

  /* =========================
     NOT FOUND
  ========================= */
  if (!machine) {
    return (
      <>
        <Header />

        <div className="min-h-screen bg-[#f6f9fc] flex items-center justify-center px-4">
          <div className="relative w-full max-w-md">
            <div className="absolute -inset-[1px] rounded-[28px] bg-gradient-to-r from-blue-400/40 via-white to-teal-400/40 blur-[2px]" />

            <div className="relative bg-white rounded-[28px] border border-slate-200 p-10 text-center shadow-[0_20px_60px_rgba(15,23,42,0.10)]">
              <div className="mx-auto mb-6 w-20 h-20 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center">
                <svg
                  className="w-9 h-9 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.8"
                    d="M10.5 10.5L21 21m-6-10.5A4.5 4.5 0 1110.5 6a4.5 4.5 0 014.5 4.5z"
                  />
                </svg>
              </div>

              <h2 className="text-2xl font-bold text-slate-900">
                Machine Not Found
              </h2>

              <p className="mt-3 text-slate-500 leading-relaxed">
                The requested machine could not be found.
              </p>

              <button
                onClick={() => navigate("/machines")}
                className="mt-7 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-blue-600 transition-all duration-300"
              >
                <span>←</span>
                Back to Machinery
              </button>
            </div>
          </div>
        </div>

        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />

      <main className="min-h-screen bg-[#f6f9fc] relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-blue-400/10 blur-[100px] rounded-full pointer-events-none" />

        {/* =========================
            TOP NAVIGATION
        ========================= */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-7">
          <button
            onClick={() => navigate("/machines")}
            className="group inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600 transition-colors"
          >
            <span className="text-lg transition-transform duration-300 group-hover:-translate-x-1">
              ←
            </span>

            Back to Machinery
          </button>
        </div>

        {/* =========================
            MAIN CARD
        ========================= */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          {/* Premium border glow */}
          <div className="absolute inset-x-4 sm:inset-x-6 lg:inset-x-8 top-8 lg:top-12 bottom-8 lg:bottom-12 rounded-[32px] bg-gradient-to-r from-blue-400/30 via-white to-teal-400/30 blur-[2px]" />

          <div className="relative bg-white/95 backdrop-blur-xl rounded-[32px] border border-slate-200/80 shadow-[0_25px_80px_rgba(15,23,42,0.10)] overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr]">

              {/* =================================
                  IMAGE SECTION
              ================================= */}
              <div className="relative min-h-[420px] lg:min-h-[650px] bg-gradient-to-br from-slate-50 via-white to-blue-50/40 border-b lg:border-b-0 lg:border-r border-slate-100">

                {/* Image glow */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-blue-400/10 blur-[80px] rounded-full" />

                {/* Top label */}
                <div className="absolute top-6 left-6 z-10">
                  <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/90 border border-slate-200 shadow-sm">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="text-xs font-bold tracking-wide text-slate-600 uppercase">
                      Industrial Machinery
                    </span>
                  </div>
                </div>

                {/* Image */}
                <div className="relative h-full min-h-[420px] lg:min-h-[650px] flex items-start justify-center p-2 lg:p-12">
                  {machine.image ? (
                    <img
                      src={machine.image}
                      alt={machine.name}
                      className="relative z-10 w-full h-full max-h-[570px] object-contain drop-shadow-[0_25px_30px_rgba(15,23,42,0.12)] transition-transform duration-500 hover:scale-[1.02]"
                    />
                  ) : (
                    <div className="relative z-10 w-32 h-32 rounded-full bg-white border border-slate-200 shadow-xl flex items-center justify-center">
                      <svg
                        className="w-14 h-14 text-slate-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                          d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-5h6v5M9 9h.01M12 9h.01M15 9h.01M9 12h.01M12 12h.01M15 12h.01"
                        />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Bottom decorative line */}
                <div className="absolute bottom-0 left-12 right-12 h-px bg-gradient-to-r from-transparent via-blue-300/50 to-transparent" />
              </div>

              {/* =================================
                  DETAILS SECTION
              ================================= */}
              <div className="p-7 sm:p-9 lg:p-12 xl:p-14 flex flex-col justify-center">

                {/* Category */}
                {category?.name && (
                  <div className="mb-5">
                    <span className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-xs font-bold uppercase tracking-wider">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                      {category.name}
                    </span>
                  </div>
                )}

                {/* Machine Name */}
                <h1 className="text-3xl sm:text-4xl lg:text-[44px] xl:text-[48px] leading-[1.08] font-extrabold tracking-tight text-slate-900">
                  {machine.name}
                </h1>

                {/* Accent line */}
                <div className="mt-6 flex items-center gap-2">
                  <div className="w-12 h-[3px] rounded-full bg-blue-600" />
                  <div className="w-3 h-[3px] rounded-full bg-teal-500" />
                </div>

                {/* Description */}
                {machine.description && (
                  <div className="mt-8">
                    <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-3">
                      Description
                    </h2>

                    <p className="text-[15px] sm:text-base text-slate-600 leading-7">
                      {machine.description}
                    </p>
                  </div>
                )}

                {/* Specification */}
                {machine.specification && (
                  <div className="mt-8">
                    <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-3">
                      Specifications
                    </h2>

                    <div className="relative overflow-hidden rounded-2xl bg-slate-50 border border-slate-200">
                      {/* Small accent */}
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-teal-500" />

                      <div className="p-5 sm:p-6">
                        <p className="text-sm sm:text-[15px] text-slate-600 leading-7 whitespace-pre-line">
                          {machine.specification}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Divider */}
                <div className="mt-9 border-t border-slate-100" />

                {/* Buttons */}
                <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">

                  {/* Back */}
                  <button
                    onClick={() => navigate(-1)}
                    className="group flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl border border-slate-200 bg-white text-slate-700 font-semibold text-sm hover:border-slate-300 hover:bg-slate-50 transition-all duration-300"
                  >
                    <span className="transition-transform duration-300 group-hover:-translate-x-1">
                      ←
                    </span>
                    Back
                  </button>

                  {/* PDF */}
                  {machine.pdf && (
                    <a
                      href={machine.pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-slate-900 text-white font-semibold text-sm hover:bg-blue-600 shadow-lg shadow-slate-900/10 hover:shadow-blue-500/20 transition-all duration-300"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.8"
                          d="M12 3v12m0 0l4-4m-4 4l-4-4M5 21h14"
                        />
                      </svg>

                      Specifications
                    </a>
                  )}

                  {/* Inquiry */}
                  <button
                    onClick={() => navigate("/contact")}
                    className="sm:col-span-2 group flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold text-sm shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/25 hover:from-blue-700 hover:to-blue-600 transition-all duration-300"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.8"
                        d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"
                      />
                    </svg>

                    Make an Inquiry

                    <span className="transition-transform duration-300 group-hover:translate-x-1">
                      →
                    </span>
                  </button>
                </div>

                {/* Trust text */}
                <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-400">
                  <svg
                    className="w-4 h-4 text-emerald-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>

                  Professional Industrial Machinery Solutions
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default MachineDetails;